/** Utilidades puras sobre la transcripción. */
import { speakerDisplayName, type Speaker, type TranscriptSegment } from '@eco/shared'

const norm = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()

/** Índices de segmentos que contienen la consulta (sin tildes ni mayúsculas). */
export function findMatches(segments: TranscriptSegment[], q: string): number[] {
  const n = norm(q.trim())
  if (n.length < 2) return []
  const out: number[] = []
  segments.forEach((s, i) => {
    if (norm(s.texto).includes(n) || norm(s.speaker).includes(n)) out.push(i)
  })
  return out
}

/**
 * Segmento que se está diciendo en `ms` (o el siguiente, si cae en un
 * silencio). Búsqueda binaria: los segmentos vienen ordenados por inicio.
 */
export function segmentAt(segments: TranscriptSegment[], ms: number): number {
  if (!segments.length) return -1
  let lo = 0
  let hi = segments.length - 1
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (segments[mid]!.inicioMs <= ms) lo = mid
    else hi = mid - 1
  }
  const s = segments[lo]!
  if (s.inicioMs <= ms && ms < s.finMs) return lo
  if (ms < s.inicioMs) return lo
  return Math.min(lo + 1, segments.length - 1)
}

/** Nombre mostrable → hablante (los segmentos ya traen el nombre resuelto). */
export function speakerByName(speakers: Speaker[], name: string): Speaker | undefined {
  return speakers.find((s) => speakerDisplayName(s.etiqueta, s.nombre) === name)
}

/**
 * Traduce una etiqueta estable de las notas ('Yo', 'Participante 2') al nombre
 * que el usuario le puso al hablante, si lo hizo.
 */
export function labelToName(speakers: Speaker[], etiqueta: string): string {
  const sp = speakers.find((s) => speakerDisplayName(s.etiqueta) === etiqueta)
  return sp ? speakerDisplayName(sp.etiqueta, sp.nombre) : etiqueta
}

/** Minutos hablados por cada hablante (para la ficha de la reunión). */
export function talkTime(
  segments: TranscriptSegment[]
): { speaker: string; ms: number; share: number }[] {
  const by = new Map<string, number>()
  for (const s of segments)
    by.set(s.speaker, (by.get(s.speaker) ?? 0) + Math.max(0, s.finMs - s.inicioMs))
  const total = [...by.values()].reduce((a, b) => a + b, 0) || 1
  return [...by.entries()]
    .map(([speaker, ms]) => ({ speaker, ms, share: ms / total }))
    .sort((a, b) => b.ms - a.ms)
}

export interface SpeakerStats {
  speaker: string
  ms: number
  share: number
  /** Veces que tomó la palabra. */
  turnos: number
  /** Preguntas que hizo (oraciones que terminan en «?»). */
  preguntas: number
  /** Intervención continua más larga (turnos seguidos con pausas < 2 s). */
  monologoMaxMs: number
}

/** Estadísticas por hablante, como las de tl;dv: cuánto habló, turnos, preguntas y monólogo. */
export function speakerStats(segments: TranscriptSegment[]): SpeakerStats[] {
  const by = new Map<string, SpeakerStats>()
  const get = (speaker: string) => {
    let s = by.get(speaker)
    if (!s)
      by.set(speaker, (s = { speaker, ms: 0, share: 0, turnos: 0, preguntas: 0, monologoMaxMs: 0 }))
    return s
  }
  let turno: { speaker: string; inicio: number; fin: number } | null = null
  const cerrar = () => {
    if (!turno) return
    const s = get(turno.speaker)
    s.turnos++
    s.monologoMaxMs = Math.max(s.monologoMaxMs, turno.fin - turno.inicio)
  }
  for (const seg of segments) {
    const s = get(seg.speaker)
    s.ms += Math.max(0, seg.finMs - seg.inicioMs)
    s.preguntas += (seg.texto.match(/\?/g) ?? []).length
    if (turno && turno.speaker === seg.speaker && seg.inicioMs - turno.fin < 2000) {
      turno.fin = Math.max(turno.fin, seg.finMs)
      continue
    }
    cerrar()
    turno = { speaker: seg.speaker, inicio: seg.inicioMs, fin: seg.finMs }
  }
  cerrar()
  const total = [...by.values()].reduce((a, b) => a + b.ms, 0) || 1
  return [...by.values()].map((s) => ({ ...s, share: s.ms / total })).sort((a, b) => b.ms - a.ms)
}

/** Deja solo letras y números separados por un espacio, con bordes, para buscar palabras. */
const words = (s: string) =>
  ` ${norm(s)
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()} `

/**
 * Segmentos donde aparece alguno de los términos de un tema, al comienzo de una
 * palabra («precio» encuentra «precios», no «aprecio»; «acme inc» es una frase).
 */
export function trackerHits(segments: TranscriptSegment[], terminos: string[]): number[] {
  const ts = terminos.map((t) => words(t).trimEnd()).filter((t) => t.trim().length >= 2)
  if (!ts.length) return []
  const out: number[] = []
  segments.forEach((s, i) => {
    const w = words(s.texto)
    if (ts.some((t) => w.includes(t))) out.push(i)
  })
  return out
}
